import { Controller } from '@hotwired/stimulus'
import * as focusTrap from 'focus-trap'

export default class extends Controller {
    static targets = ['clickOutside']
    static classes = ['active']
    static outlets = ['toggle-sender']
    static values = {
        focusTrap: Boolean,
        setHeight: Boolean,
    }

    #focusTrap = null
    #removeClasses = []

    connect() {
        if (!this.element.id) {
            this.element.setAttribute('id', (Math.random() + 1).toString(36).substring(7))
        }

        if (!this.element.hasAttribute('tabindex')) {
            this.element.setAttribute('tabindex', '-1')
        }

        if (this.focusTrapValue) {
            this.#focusTrap = focusTrap.createFocusTrap(this.element, {
                escapeDeactivates: true,
                allowOutsideClick: true,
                initialFocus: false,
            })
        }

        if (this.hasActiveClass && !this.element.classList.contains(this.activeClass)) {
            this.#removeClasses.push(this.activeClass)
        }

        if (this.hasInactiveClass && !this.element.classList.contains(this.inactiveClass)) {
            this.#removeClasses.push(this.inactiveClass)
        }
    }

    disconnect() {
        for (const removeClass of this.#removeClasses) {
            this.element.classList.remove(removeClass)
        }

        this.#focusTrap?.deactivate()
    }

    toggle(event) {
        this.#toggleState(!this.isOpen(), event)
    }

    open(event) {
        if (this.isOpen()) {
            return
        }

        this.#toggleState(true, event)
    }

    close(event) {
        if (!this.isOpen()) {
            return
        }

        this.#toggleState(false, event)
    }

    documentClick(event) {
        if (
            !this.isOpen() ||
            this.toggleSenderOutlets.filter((t) => t.element.contains(event.target)).length > 0 ||
            (this.hasClickOutsideTarget ? this.clickOutsideTarget.contains(event.target) : this.element.contains(event.target))
        ) {
            return
        }

        this.#toggleState(false)
    }

    isOpen() {
        if (this.hasToggleSenderOutlet) {
            return 'true' === this.toggleSenderOutlet.element.ariaExpanded
        }

        if (this.hasActiveClass) {
            return this.element.classList.contains(this.activeClass)
        }

        if (this.hasInactiveClass) {
            return !this.element.classList.contains(this.inactiveClass)
        }

        return false;
    }

    #toggleState(state, event = null) {
        if (this.setHeightValue) {
            this.element.style.height = `${state ? this.element.scrollHeight : 0}px`
        }

        if (this.hasActiveClass) {
            this.element.classList.toggle(this.activeClass, state)
        }

        if (this.hasInactiveClass) {
            this.element.classList.toggle(this.inactiveClass, !state)
        }

        if (state) {
            setTimeout(() => this.#focusTrap?.activate(), 200)
        } else {
            this.#focusTrap?.deactivate()
        }

        for (const handler of this.toggleSenderOutlets) {
            handler.setState(state)
        }

        this.dispatch('toggled', {
            detail: state
        })

        if (state) {
            this.dispatch('opened')
        } else {
            this.dispatch('closed')
        }

        if (['mouseenter', 'mouseover', 'mouseleave'].includes(event?.type)) {
            return
        }

        const focusDelay = event?.params?.focusDelay ?? 50;

        // Focus first input immediately, if requested and present
        if (state && (event?.params?.focusInput ?? false)) {
            const input = this.element.querySelector('input:not([type="hidden"]),textarea');

            if (input) {
                if (0 === focusDelay) {
                    input.focus();
                } else {
                    setTimeout(() => input.focus(), focusDelay)
                }

                setTimeout(() => input.setSelectionRange(-1, -1), 0)

                return
            }
        }

        const applyFocus = () => {
            if (state) {
                this.element.focus()
            } else {
                if (this.hasToggleSenderOutlet) {
                    this.toggleSenderOutlet.element.focus()
                }
            }
        }

        if (0 === focusDelay) {
            applyFocus()
        } else {
            setTimeout(applyFocus, focusDelay)
        }
    }
}
